import { FC, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BUSINESS_TOOL_MODE } from 'constants/businessTool';
import { BUSINESS_TOOL } from 'constants/paths';
import { WorkspaceLayout } from 'layouts/WorkspaceLayout';
import { useCreateBusinessPlan, useGetAllBusinessPlans } from 'services/businessPlan/hooks';
import { useGetPaymentsHistory } from 'services/payments/payments/hooks';
import { useGetSubscriptionDetails } from 'services/payments/subscriptions/hooks';
import { getDefaultBusinessPlan, useBusinessToolSessionStore } from 'store/useBusinessToolStore';
import { getSetBusinessToolMode } from 'store/useBusinessToolStore/selectors';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { IBusinessPlanSectionEntity, IBusinessPlanType } from 'interfaces/businessPlan';
import { IUserSubscriptionResponse } from 'interfaces/subscriptions';

import { PageLoader } from 'components/_basic/PageLoader/PageLoader';

import { insertId } from 'helpers/path';

export const Workspace: FC = () => {
  const { data: businessPlansData, error, isPending } = useGetAllBusinessPlans();
  const authUser = useUserStore(getAuthUser);
  const { isAuthenticated } = useAuthenticated();
  const setBusinessToolMode = useBusinessToolSessionStore(getSetBusinessToolMode);
  const { data: paymentData } = useGetPaymentsHistory({ page: 0, size: 10, delay: 400 });
  const [searchParams] = useSearchParams();
  const createBusinessPlan = useCreateBusinessPlan();
  const defaultBusinessPlan = getDefaultBusinessPlan();
  const navigate = useNavigate();

  const { data: subscriptionData } = useGetSubscriptionDetails({ enabled: !!authUser && isAuthenticated });

  const [loading, setLoading] = useState(true);

  const [sections, setSections] = useState<IBusinessPlanSectionEntity[] | null>(null);

  const projectBriefDescription = localStorage.getItem('projectBriefDescription');
  const companyBriefDescription = localStorage.getItem('companyBriefDescription');
  const address = localStorage.getItem('address');
  const companyName = localStorage.getItem('companyName');
  const onlyPayedSignUp = localStorage.getItem('onlyPayedSignUp');

  useEffect(() => {
    const newObj: Record<string, string> = {};
    const language = localStorage.getItem('language');

    if (projectBriefDescription) {
      newObj['projectBriefDescription'] = projectBriefDescription;
    }
    if (companyBriefDescription) {
      newObj['companyBriefDescription'] = companyBriefDescription;
    }
    if (address) {
      newObj['address'] = address;
    }
    if (companyName) {
      newObj['companyName'] = companyName;
    }
    if (language) {
      newObj['documentLanguage'] = language === 'de' ? 'Deutsch' : 'English';
    }

    const formattedSections: IBusinessPlanSectionEntity[] = [
      { chapterName: 'GLOBAL', content: JSON.stringify(newObj) },
    ];
    setSections(formattedSections);
    return () => {
      setSections([]);
    };
  }, []);

  const createBP = useCallback(
    async ({ type, sections }: { type: IBusinessPlanType; sections: IBusinessPlanSectionEntity[] }) => {
      try {
        const newBusinessPlan = await createBusinessPlan.mutateAsync({
          title: 'Document',
          type,
          sections,
          metadata: defaultBusinessPlan.metadata,
        });

        navigate(insertId(BUSINESS_TOOL, newBusinessPlan.uuid));
        localStorage.removeItem('typeFormDataId');
      } catch (error) {
        console.error(error);
      }
    },
    [sections],
  );

  useEffect(() => {
    if (onlyPayedSignUp) {
      return;
    }
    if (
      businessPlansData &&
      (subscriptionData as IUserSubscriptionResponse)?.currentSubscription?.subscriptionPlan === 'FREE' &&
      !businessPlansData?.content?.find((item) => item.type === 'BANK' || item.type === 'LIGHT') &&
      sections
    ) {
      setBusinessToolMode(BUSINESS_TOOL_MODE.EDIT);
      createBP({ type: 'BANK', sections: sections });
    } else if (
      searchParams.get('newTarif') &&
      subscriptionData &&
      businessPlansData &&
      (subscriptionData as IUserSubscriptionResponse)?.currentSubscription?.subscriptionPlan !== 'STARTER' &&
      !businessPlansData?.content?.find((item) => item.type === 'BANK' || item.type === 'LIGHT') &&
      sections
    ) {
      setBusinessToolMode(BUSINESS_TOOL_MODE.EDIT);
      createBP({ type: 'BANK', sections: sections });
    } else if (subscriptionData && businessPlansData) {
      setLoading(false);
    }
  }, [searchParams.get('newTarif'), subscriptionData, businessPlansData, sections]);

  useEffect(() => {
    if (paymentData && paymentData?.content.length > 0) {
      const currentTime = new Date().getTime();
      const paymentTime = new Date(paymentData.content[0].createdAt).getTime();
      const tenSecondsInMs = 10 * 1000;

      const isWithinTenSeconds = currentTime < paymentTime + tenSecondsInMs;
      const itemName = paymentData.content[0].order.subscription.name.split(' ')[0];

      if (isWithinTenSeconds && (itemName === 'Light' || itemName === 'Standard')) {
        // Handle the case when we're within 10 seconds of payment time
        const operationId = paymentData.content[0].operationId;
        const currency = paymentData.content[0].order.subscription.currency.toUpperCase();
        const price = paymentData.content[0].order.subscription.price / 100;
        const itemVariant =
          paymentData.content[0].order.subscription.name.split(' ')[1] === 'Annually'
            ? 'Billed annually'
            : 'Billed monthly';
        const itemId =
          paymentData.content[0].order.subscription.name.split(' ')[1] === 'Annually'
            ? `${itemName.toLowerCase()}-annually`
            : `${itemName.toLowerCase()}-monthly`;

        (window as any).dataLayer?.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: operationId,
            value: price,
            currency: currency,
            items: [
              {
                item_name: itemName,
                item_variant: itemVariant,
                item_id: itemId,
                price: price,
              },
            ],
          },
        });
      }
    }
  }, [paymentData]);

  return (
    <>
      <Helmet>
        <script type="text/javascript" defer>
          {`
            window.dataLayer = window.dataLayer || [];
            let userId = '';
            try {
              const userStore = localStorage.getItem('user-store');
              if (userStore) {
                const parsed = JSON.parse(userStore);
                userId = parsed?.state?.authUser?.id || '';
                dataLayer.push({ user_id: userId });
              }
            } catch (e) {
              console.error('Failed to parse user-store:', e);
            }
          `}
        </script>
        <script type="text/javascript" defer>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                          })(window,document,'script','dataLayer','GTM-PB57NZW9');`}
        </script>
        <script type="text/javascript" defer>
          {`!function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '951508482576405');
            fbq('track', 'PageView');`}
        </script>
      </Helmet>
      {loading ? <PageLoader /> : <WorkspaceLayout data={businessPlansData} isLoading={isPending} error={error} />}
    </>
  );
};
